/**
 * Endpoint discovery + a timed fetch that never throws.
 *
 * HONESTY (INVARIANT #10): the resolution SOURCE is returned alongside the URLs and displayed,
 * so the page can never misrepresent which backend produced a number. Latency is only ever
 * reported for a real network call, and it is always labelled as a browser-observed round trip.
 */

export const CONTRACT_DEFAULTS = {
  serialApi: 'https://serial.awssa.net',
  rulesApi: 'https://rules.awssa.net',
};

export const STORAGE_KEY = 'demo-endpoints';

/**
 * The key this one replaced. Read as a fallback, then migrated forward and removed.
 *
 * ⚠️ MIGRATED RATHER THAN RESET. Saved endpoints are the only state on this site the user TYPED —
 * someone pointing the page at a stub or at the second region has entered two URLs by hand, and
 * silently dropping them looks like the save button never worked. There is no visual regression here
 * (unlike the colour mode, see src/boot.js), but the reset would be a data loss for zero benefit.
 * `saveEndpoints` writes only the new key, so the legacy one dies out after one visit.
 */
const STORAGE_KEY_LEGACY = 'usps-demo-endpoints';

const KEYS_SERIAL = ['serialApi', 'serialApiUrl', 'SerialApiUrl', 'SerialApiEndpoint', 'SerialApiExecuteUrl'];
const KEYS_RULES = ['rulesApi', 'rulesApiUrl', 'RulesApiUrl', 'RulesApiEndpoint', 'RulesApiExecuteUrl'];

const pick = (obj, keys) => {
  for (const k of keys) if (obj && typeof obj[k] === 'string' && obj[k]) return obj[k];
  return null;
};

/**
 * Resolution order, most specific first:
 *   1. ?serialApi= / ?rulesApi= query parameters (points the page at a stub for verification)
 *   2. localStorage — whatever was saved in the Endpoints panel
 *   3. outputs/*.json written by the deploy (build.mjs copies them next to the bundle)
 *   4. the contract defaults in docs/api-contract.md
 */
export async function resolveEndpoints() {
  const params = new URLSearchParams(window.location.search);
  const qsSerial = params.get('serialApi');
  const qsRules = params.get('rulesApi');
  if (qsSerial || qsRules) {
    return {
      serialApi: strip(qsSerial) || CONTRACT_DEFAULTS.serialApi,
      rulesApi: strip(qsRules) || CONTRACT_DEFAULTS.rulesApi,
      source: 'query-parameter',
    };
  }

  try {
    let saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!saved || (!saved.serialApi && !saved.rulesApi)) {
      // Not migrated yet — read the legacy key and copy it forward, so this runs once per browser.
      const legacy = JSON.parse(localStorage.getItem(STORAGE_KEY_LEGACY) || 'null');
      if (legacy && (legacy.serialApi || legacy.rulesApi)) {
        saved = legacy;
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(legacy));
          localStorage.removeItem(STORAGE_KEY_LEGACY);
        } catch (_e2) {
          /* the migration will not persist; the values still apply for this session */
        }
      }
    }
    if (saved && (saved.serialApi || saved.rulesApi)) {
      return {
        serialApi: saved.serialApi || CONTRACT_DEFAULTS.serialApi,
        rulesApi: saved.rulesApi || CONTRACT_DEFAULTS.rulesApi,
        source: 'saved-in-browser (localStorage)',
      };
    }
  } catch (_e) {
    /* storage unavailable — fall through */
  }

  const candidates = [
    'outputs/all.json',
    'outputs/api.json',
    'outputs/endpoints.json',
    '../outputs/all.json',
    '../outputs/api.json',
  ];
  for (const url of candidates) {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) continue;
      const json = await res.json();
      // all.json nests per-stack objects one level deep; api.json is flat. Accept both.
      const shapes = [json, ...Object.values(json).filter((v) => v && typeof v === 'object')];
      let serial = null;
      let rules = null;
      for (const s of shapes) {
        serial = serial || pick(s, KEYS_SERIAL);
        rules = rules || pick(s, KEYS_RULES);
      }
      if (serial || rules) {
        return {
          serialApi: serial || CONTRACT_DEFAULTS.serialApi,
          rulesApi: rules || CONTRACT_DEFAULTS.rulesApi,
          source: `deploy outputs (${url})`,
        };
      }
    } catch (_e) {
      /* not deployed yet, or not served alongside the bundle — expected */
    }
  }

  return {
    ...CONTRACT_DEFAULTS,
    source: 'contract default (docs/api-contract.md)',
  };
}

const strip = (u) => (u ? String(u).trim().replace(/\/+$/, '') : u);

/** Timed fetch. Returns {ok, status, json, text, ms, error} and NEVER throws. */
export async function timedFetch(url, options = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const t0 = performance.now();
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    const ms = performance.now() - t0;
    let json = null;
    let text = null;
    try {
      text = await res.text();
      json = text ? JSON.parse(text) : null;
    } catch (_e) {
      /* non-JSON body — keep the raw text for the error message */
    }
    return { ok: res.ok, status: res.status, json, text, ms };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      json: null,
      ms: performance.now() - t0,
      error: err.name === 'AbortError' ? `timed out after ${timeoutMs}ms` : err.message,
    };
  } finally {
    clearTimeout(timer);
  }
}

export function saveEndpoints(serialApi, rulesApi) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ serialApi, rulesApi }));
  } catch (_e) {
    /* ignore — the values still apply for this session */
  }
}

export function clearSavedEndpoints() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Also clear the legacy key: "Reset" must actually reset, and leaving the old value behind
    // would let the migration above resurrect it on the next load.
    localStorage.removeItem(STORAGE_KEY_LEGACY);
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Extract a human error from a failed contract response.
 * Errors carry `{error, code}` per docs/api-contract.md; anything else is surfaced raw rather
 * than replaced with a friendly lie.
 */
export function errorText(res) {
  const code = res.json && res.json.code ? ` [${res.json.code}]` : '';
  const msg = (res.json && res.json.error) || res.error || res.text || 'request failed';
  return res.status ? `HTTP ${res.status}${code}: ${msg}` : `${msg}`;
}

/**
 * A deployed-but-not-implemented API answers 200 with `placeholder: true` (the infra layer's
 * stand-in Lambda). That is reachable, so a naive health check calls it LIVE — and then the UI
 * would show an empty serial list as a successful vend. Detect it explicitly instead.
 */
export const isPlaceholder = (json) => Boolean(json && json.placeholder === true);
